// React
import { useMemo } from "react"

// Router
import { Routes } from "@/constants/routes"

// UI
import { PageTemplate } from "@/layouts/PageTemplate"
import { CardWrapper, CardBody } from "@/components/Card"
import { Heading, P } from "@/components/Typography"
import {
	GraphData,
	MultiLineGraph,
	TooltipComponentProps,
	TooltipComponentType,
} from "@/components/graphs/types/multi-line/MultiLineGraph"
import { Text } from "@visx/text"
import {
	Table,
	TableBody,
	TableDataCell,
	TableHead,
	TableRowCell,
	TableHeading,
} from "@/components/table-controls/TableItems"

// Components
import { AllProjectsDefaultRate } from "../_components/AllProjectsDefaultRate"

// Translate
import { useLang } from "@/context/lang"
import { useTrans, Trans } from "@/lib/i18n"
import { useForceDefaultLanguage } from "@/hooks/useForceDefaultLanguage"

// Types
import { UsersUserProfileLanguageChoices } from "@/api/graphql/types"
interface DefaultRateData {
	year: string
	defaultRate: number
	numerator: number
	denominator: number
}

// All data that will eventually be pulled from API calls
const API_DATA = {
	defaultRates: [
		{ year: "2022", defaultRate: 2.46, numerator: 3, denominator: 122 },
		{ year: "2023", defaultRate: 5.65, numerator: 7, denominator: 124 },
		{ year: "2024", defaultRate: 5.94, numerator: 6, denominator: 101 },
	] as DefaultRateData[],
}

export function StatisticsDefaultRateInfo() {
	// Translations
	const t = useTrans("dashboard")
	const { DefaultLanguageDisclaimerComponent } = useForceDefaultLanguage(
		UsersUserProfileLanguageChoices.En,
	)

	// Data that will come from API
	const defaultRateData: DefaultRateData[] = useMemo(
		() => API_DATA.defaultRates,
		[],
	)

	// Calculate average of all default rates
	const averageRate = useMemo(() => {
		if (defaultRateData.length === 0) return "0"
		const sum = defaultRateData.reduce(
			(acc, data) => acc + data.defaultRate,
			0,
		)
		const average = sum / defaultRateData.length
		return average.toFixed(2)
	}, [defaultRateData])

	const graphData = useMemo(
		() =>
			defaultRateData.map((entry) => ({
				x: entry.year,
				y: entry.defaultRate,
			})) as GraphData[],
		[defaultRateData],
	)

	const lineDatas = useMemo(
		() => [
			{
				data: graphData,
				id: "default_rate",
				variant: "primary" as const,
			},
		],
		[graphData],
	)

	return (
		<>
			<title>
				{t("common:common.web.title", {
					page: t(
						"dashboard:dashboard.statistics.default_rate.title",
					),
				})}
			</title>

			<PageTemplate backHref={Routes.StatisticsPlatform}>
				<DefaultLanguageDisclaimerComponent />

				<AllProjectsDefaultRate />
				<CardWrapper className="mb-4">
					<CardBody>
						<Heading as="h2" className="mb-8 text-center">
							{t("dashboard.statistics.default_rate.title")}
						</Heading>

						<div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
							{/* Chart Section */}
							<div className="lg:col-span-2">
								<div className="h-80 w-full">
									<MultiLineGraph
										lineDatas={lineDatas}
										margin={{
											left: 60,
											right: 25,
											top: 25,
											bottom: 50,
										}}
										TooltipComponent={DefaultRateTooltip}
										xTickComponent={({
											formattedValue,
											...props
										}) => {
											// Only show years that match our actual data: 2022, 2023, 2024
											if (
												formattedValue &&
												typeof formattedValue ===
													"string"
											) {
												// Show 2023 and 2024 when they appear naturally
												if (
													formattedValue === "2023" ||
													formattedValue === "2024"
												) {
													return (
														<Text {...props}>
															{formattedValue}
														</Text>
													)
												}

												// For the first tick position only, force 2022 since scale doesn't generate it
												const x = props.x || 0
												// Show 2022 for first tick: desktop ~82px, mobile ~43px, but not mobile second tick ~87px
												if (
													(x < 50 ||
														(x > 75 && x < 85)) &&
													formattedValue !== "2023" &&
													formattedValue !== "2024"
												) {
													return (
														<Text {...props}>
															2022
														</Text>
													)
												}

												// Hide everything else (months, other years, etc.)
												return <Text {...props}></Text>
											}
											return <Text {...props}></Text>
										}}
										yTickComponent={({
											formattedValue,
											...props
										}) => (
											<Text {...props}>
												{formattedValue
													? `${formattedValue}%`
													: ""}
											</Text>
										)}
									/>
								</div>
							</div>

							{/* Explanation Section */}
							<div className="rounded-lg border border-gray-200 bg-gray-50 p-6">
								<P className="text-sm leading-relaxed text-gray-700">
									{t(
										"dashboard.statistics.default_rate.explanation",
									)}
								</P>
							</div>
						</div>
					</CardBody>
				</CardWrapper>

				<CardWrapper className="mb-4">
					<CardBody className="text-center">
						<Trans
							ns="dashboard"
							i18nKey="dashboard.statistics.default.average_rate_text"
							values={{
								rate: averageRate,
							}}
							components={{
								strong: <strong />,
							}}
						/>
						<P className="text-sm text-gray-600">
							{t("dashboard.statistics.default_rate.disclaimer")}
						</P>
					</CardBody>
				</CardWrapper>

				<CardWrapper>
					<CardBody>
						<Table>
							<TableHead>
								<TableRowCell withHover={false}>
									<TableHeading variant="static">
										{t(
											"dashboard.statistics.default_rate.table.year",
										)}
									</TableHeading>
									<TableHeading variant="static">
										{t(
											"dashboard.statistics.default_rate.table.numerator",
										)}
									</TableHeading>
									<TableHeading variant="static">
										{t(
											"dashboard.statistics.default_rate.table.denominator",
										)}
									</TableHeading>
									<TableHeading variant="static">
										{t(
											"dashboard.statistics.default_rate.table.default_rate",
										)}
									</TableHeading>
								</TableRowCell>
							</TableHead>
							<TableBody>
								{defaultRateData.map((row, index) => (
									<TableRowCell
										key={row.year}
										isOdd={index % 2 === 1}
										withHover={false}
									>
										<TableDataCell>
											{row.year}
										</TableDataCell>
										<TableDataCell>
											{row.numerator}
										</TableDataCell>
										<TableDataCell>
											{row.denominator}
										</TableDataCell>
										<TableDataCell>
											{row.defaultRate}
										</TableDataCell>
									</TableRowCell>
								))}
							</TableBody>
						</Table>
					</CardBody>
				</CardWrapper>
			</PageTemplate>
		</>
	)
}

const DefaultRateTooltip: TooltipComponentType = ({
	label,
	value,
}: TooltipComponentProps) => {
	// i18n
	const { formatNumber } = useLang()
	const t = useTrans("dashboard")

	return (
		<div className="px-4 py-3">
			<Heading as="h3" styleAs="h5" className="text-black">
				{label}
			</Heading>
			<P className="flex items-center gap-x-2 text-black md:mt-1">
				<svg width={10} height={10} viewBox="0 0 10 10">
					<circle r={5} cx={5} cy={5} fill="rgb(20, 154, 229)" />
				</svg>
				<span className="font-bold">
					{t(
						"dashboard.statistics.default_rate.tooltip.default_rate",
						{ value: formatNumber(Number(value)) },
					)}
				</span>
			</P>
		</div>
	)
}
